import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, Smartphone, X, Zap, Wrench } from 'lucide-react';

// --- 1. PWA UTILITIES (Embedded for portability) ---

type PWAEvent = 'update_available' | 'install_prompt_ready' | 'app_installed' | 'online' | 'offline';
type PWACallback = () => void;

class PWAUtils {
  private static isInstalled = false;
  private static deferredPrompt: any = null;
  private static listeners: Map<PWAEvent, Set<PWACallback>> = new Map();

  // --- Event System ---
  static on(event: PWAEvent, callback: PWACallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);
    
    // Immediate check for current state on subscription
    if (event === 'offline' && !navigator.onLine) callback();
    if (event === 'install_prompt_ready' && this.deferredPrompt) callback();
  }

  static off(event: PWAEvent, callback: PWACallback) {
    this.listeners.get(event)?.delete(callback);
  }

  private static emit(event: PWAEvent) {
    this.listeners.get(event)?.forEach(cb => cb());
  }

  // --- Core Logic ---
  static init() {
    // 1. Service Worker
    const isSupportedProtocol = window.location.protocol === 'https:' || window.location.protocol === 'http:';

    if ('serviceWorker' in navigator && isSupportedProtocol) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        console.log('[PWA] Service Worker registered');

        // A. Check if there is ALREADY a waiting worker (Update was downloaded previously)
        if (reg.waiting) {
           console.log('[PWA] Found waiting worker, update available');
           this.emit('update_available');
        }
        
        // B. Listen for NEW updates found during this session
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              // If we have a controller (old SW), and new one is installed -> Update Available
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[PWA] New worker installed, update available');
                this.emit('update_available');
              }
            });
          }
        });
      }).catch(err => {
        console.warn('[PWA] Service Worker registration skipped or failed:', err);
      });
    }

    // 2. Install Prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.emit('install_prompt_ready');
    });

    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.deferredPrompt = null;
      this.emit('app_installed');
    });

    // 3. Network Status
    window.addEventListener('online', () => this.emit('online'));
    window.addEventListener('offline', () => this.emit('offline'));
  }

  static async installPWA() {
    if (!this.deferredPrompt) return;
    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      this.deferredPrompt = null;
    }
  }

  static reloadForUpdate() {
    // Attempt to skip waiting if possible, then reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (reg && reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }
}

// --- 2. MAIN COMPONENT ---

export const PWAManager = () => {
  const [status, setStatus] = useState({
    isOffline: !navigator.onLine,
    updateAvailable: false,
    canInstall: false,
    showDebug: false // Debug menu toggle
  });

  useEffect(() => {
    // Initialize listeners
    PWAUtils.init();

    const handleOffline = () => setStatus(prev => ({ ...prev, isOffline: true }));
    const handleOnline = () => setStatus(prev => ({ ...prev, isOffline: false }));
    const handleUpdate = () => setStatus(prev => ({ ...prev, updateAvailable: true }));
    const handleInstallReady = () => setStatus(prev => ({ ...prev, canInstall: true }));
    const handleInstalled = () => setStatus(prev => ({ ...prev, canInstall: false }));

    PWAUtils.on('offline', handleOffline);
    PWAUtils.on('online', handleOnline);
    PWAUtils.on('update_available', handleUpdate);
    PWAUtils.on('install_prompt_ready', handleInstallReady);
    PWAUtils.on('app_installed', handleInstalled);

    return () => {
      PWAUtils.off('offline', handleOffline);
      PWAUtils.off('online', handleOnline);
      PWAUtils.off('update_available', handleUpdate);
      PWAUtils.off('install_prompt_ready', handleInstallReady);
      PWAUtils.off('app_installed', handleInstalled);
    };
  }, []);

  // Prevent page scrolling while overlays (offline/update) are visible
  useEffect(() => {
    const shouldHide = status.isOffline || status.updateAvailable;
    if (shouldHide) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
    return;
  }, [status.isOffline, status.updateAvailable]);

  // --- FULL SCREEN OFFLINE VIEW ---
  if (status.isOffline) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#05070A] flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300 font-sans">
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full animate-pulse" />
          <div className="relative bg-[#0A0C10] p-6 rounded-full border border-red-500/30 shadow-2xl shadow-red-900/20">
            <WifiOff className="w-12 h-12 text-red-500" />
          </div>
        </div>
        
        <h2 className="text-3xl font-black text-white mb-3 tracking-tight">No Internet</h2>
        <p className="text-gray-400 max-w-xs mb-8 leading-relaxed">
          You are offline. Please check your internet connection to access live market data.
        </p>

        <button 
          onClick={() => window.location.reload()}
          className="group relative px-6 py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-all active:scale-95"
        >
          <span className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
            Retry Connection
          </span>
        </button>

        {/* Debug: Force Online */}
        {process.env.NODE_ENV === 'development' && (
           <button 
             onClick={() => setStatus(prev => ({ ...prev, isOffline: false }))}
             className="mt-8 text-xs text-gray-600 hover:text-white underline"
           >
             [Debug] Force Online State
           </button>
        )}
      </div>
    );
  }

  // --- FULL SCREEN UPDATE VIEW ---
  if (status.updateAvailable) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#05070A]/95 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-center animate-in slide-in-from-bottom duration-500 font-sans">
         <div className="w-full max-w-sm bg-[#0A0C10] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-600" />
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/10 blur-3xl rounded-full" />

            <div className="flex flex-col items-center relative z-10">
              <div className="bg-emerald-500/10 p-4 rounded-2xl mb-6 border border-emerald-500/20">
                <Zap className="w-8 h-8 text-green-500" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-2">Update Available</h2>
              <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                A new version of StockRecos is ready. Update now for the latest market features and performance improvements.
              </p>

              <div className="w-full space-y-3">
                <button 
                  onClick={() => PWAUtils.reloadForUpdate()}
                  className="w-full py-3.5 bg-green-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/20 active:scale-95 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Update Now
                </button>
                
                {/* <button 
                  onClick={() => setStatus(prev => ({ ...prev, updateAvailable: false }))}
                  className="w-full py-3.5 bg-transparent hover:bg-white/5 text-gray-400 font-medium rounded-xl transition-all"
                >
                  Not Now
                </button> */}
              </div>
            </div>
         </div>
      </div>
    );
  }

  // --- MODERN INSTALL PROMPT (Bottom Sheet) ---
  return (
    <>
      {status.canInstall && (
        <div className="fixed bottom-6 left-4 right-4 z-50 flex justify-center animate-in slide-in-from-bottom-10 fade-in duration-500 font-sans">
          <div className="bg-[#0A0C10]/90 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-2xl max-w-md w-full flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-500 rounded-xl p-2.5 shadow-lg shadow-emerald-500/20">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Install App</h3>
                <p className="text-xs text-gray-400">Get better performance</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setStatus(prev => ({ ...prev, canInstall: false }))}
                className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <button 
                onClick={() => {
                  PWAUtils.installPWA();
                  setStatus(prev => ({ ...prev, canInstall: false }));
                }}
                className="px-4 py-2 bg-white text-black text-sm font-bold rounded-lg hover:bg-gray-200 transition-colors shadow-lg"
              >
                Install
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- DEVELOPER DEBUG TOOLS (Bottom Left) --- */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 z-[9999] font-sans">
          <button 
             onClick={() => setStatus(prev => ({ ...prev, showDebug: !prev.showDebug }))}
             className="bg-gray-800 text-white p-2 rounded-full shadow-lg opacity-50 hover:opacity-100 transition-opacity"
             title="Toggle PWA Debug Menu"
          >
            <Wrench className="w-4 h-4" />
          </button>
          
          {status.showDebug && (
            <div className="absolute bottom-12 left-0 bg-[#111] border border-white/10 p-3 rounded-lg shadow-xl w-48 space-y-2 animate-in fade-in slide-in-from-bottom-2">
               <div className="text-[10px] uppercase font-bold text-gray-500 mb-1 border-b border-white/10 pb-1">Debug PWA UI</div>
               <button 
                 onClick={() => setStatus(prev => ({ ...prev, updateAvailable: !prev.updateAvailable }))}
                 className="w-full text-left text-xs text-emerald-400 hover:bg-white/5 p-1 rounded"
               >
                 Toggle Update Prompt
               </button>
               <button 
                 onClick={() => setStatus(prev => ({ ...prev, isOffline: !prev.isOffline }))}
                 className="w-full text-left text-xs text-red-400 hover:bg-white/5 p-1 rounded"
               >
                 Toggle Offline Mode
               </button>
               <button 
                 onClick={() => setStatus(prev => ({ ...prev, canInstall: !prev.canInstall }))}
                 className="w-full text-left text-xs text-blue-400 hover:bg-white/5 p-1 rounded"
               >
                 Toggle Install Bar
               </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PWAManager;